/* =====================================================================
 * WorkspaceSplitLayout — 워크스페이스 분할 레이아웃
 *
 * 프리뷰 없음 + 메시지 없음 → primaryContent 전체 표시
 * 프리뷰 있음 (와이드 PC)  → 좌(60%): 프리뷰, 우(40%): 탭 컨테이너
 * 프리뷰 있음 (좁은 PC)    → narrowView='preview' 시 렌더링만 / 'chat' 시 채팅만
 * 메시지 있음              → 탭 컨테이너에 탭 추가
 * 채팅 시작 전             → 탭 바 숨김
 * ===================================================================== */

import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { useChatStore } from '@auw/stores';
import { useConversationRuntime } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import type { ReplyToInfo } from './bubble/MessageBubble';
import { useScrollToHashMessageByIndex } from './useScrollToHashMessage';

const MessageBubble = lazy(() =>
  import('./bubble/MessageBubble').then((m) => ({ default: m.MessageBubble })),
);
import { MessageSkeleton, FilePreviewSkeleton } from '@auw/workspace-ui';

const FilePreviewPanel = lazy(() =>
  import('@auw/workspace-ui').then((m) => ({ default: m.FilePreviewPanel })),
);

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
`;

/** 좁은 콘텐츠 영역 기준 (실제 채팅 영역 너비 기준, 사이드바 제외) */
const NARROW_CONTENT_WIDTH = 1280;

/**
 * 좌측 프리뷰 래퍼
 * isNarrow는 JS(ResizeObserver)로 제어하므로 CSS 미디어쿼리 불필요
 * fullscreen 시 flex:1로 전체 공간 차지
 */
const PreviewPanel = styled.div<{ $isNarrow?: boolean; $narrowHidden?: boolean; $fullscreen?: boolean }>`
  display: ${({ $narrowHidden }) => ($narrowHidden ? 'none' : 'flex')};
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  animation: ${fadeIn} 0.25s ease;

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      flex: 1;
      padding: 10px;
    `}

  ${({ $isNarrow, $fullscreen }) =>
    !$fullscreen &&
    ($isNarrow
      ? css`
          flex: 1;
          padding: 10px;
        `
      : css`
          flex: 6;
          padding: 10px 0 16px 10px;
        `)}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const PreviewFallbackCard = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 4px 16px rgba(0, 0, 0, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.04);
`;

const PreviewFallbackHeader = styled.div`
  flex-shrink: 0;
  height: 48px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const PreviewFallbackContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const PreviewPanelFallback: React.FC = () => (
  <PreviewFallbackCard>
    <PreviewFallbackHeader />
    <PreviewFallbackContent>
      <FilePreviewSkeleton />
    </PreviewFallbackContent>
  </PreviewFallbackCard>
);

/** 우측 콘텐츠 영역 — fullscreen 시 숨김 */
const ContentArea = styled.div<{ $hasPreview: boolean; $isNarrow?: boolean; $narrowHidden?: boolean; $fullscreen?: boolean }>`
  display: ${({ $narrowHidden, $fullscreen }) => ($narrowHidden || $fullscreen ? 'none' : 'flex')};
  flex-direction: column;
  overflow: hidden;
  min-width: 0;

  ${({ $hasPreview, $isNarrow }) =>
    $isNarrow
      ? css`
          flex: 1;
          padding: ${$hasPreview ? '10px' : '0'};
        `
      : css`
          flex: ${$hasPreview ? 4 : 1};
          padding: ${$hasPreview ? '10px 10px 16px 10px' : '0'};
        `}
`;

/** 탭 컨테이너 — 프리뷰와 동일한 카드 스타일 */
const TabContainer = styled.div<{ $elevated: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;

  ${({ $elevated, theme }) =>
    $elevated &&
    css`
      background: ${theme.colors.bgPrimary};
      border-radius: 16px;
      border: 1px solid ${theme.colors.borderLight};
      box-shadow:
        0 0 0 1px rgba(0, 0, 0, 0.03),
        0 4px 16px rgba(0, 0, 0, 0.06),
        0 12px 40px rgba(0, 0, 0, 0.04);
    `}
`;

/** 탭 헤더 바 */
const TabHeader = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: 16px 16px 0 0;
`;


const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  padding: 12px 20px;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 12px;
    right: 12px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ $active, theme }) => ($active ? theme.colors.accent : 'transparent')};
    transition: background ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/** 탭 내용 래퍼 */
const TabBody = styled.div<{ $visible: boolean }>`
  flex: 1;
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  border-radius: 0 0 16px 16px;
`;

/** 채팅 메시지 스크롤 영역 */
const MessagesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.scrollbarHover};
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface WorkspaceSplitLayoutProps {
  primaryContent: React.ReactNode;
}

export const WorkspaceSplitLayout: React.FC<WorkspaceSplitLayoutProps> = ({
  primaryContent,
}) => {
  const { t } = useTranslation();
  const messages = useChatStore((s) => s.activeMessages());
  const previewFile = useChatStore((s) => s.previewFile);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const previewFullscreen = useChatStore((s) => s.previewFullscreen);
  const togglePreviewFullscreen = useChatStore((s) => s.togglePreviewFullscreen);
  const activeConv = useChatStore((s) => s.activeConversation());
  const conversationId = activeConv?.id ?? null;
  const { isStreaming: streaming } = useConversationRuntime(conversationId);
  const toggleReaction = useChatStore((s) => s.toggleReaction);
  const regenerateMessage = useChatStore((s) => s.regenerateMessage);
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');
  const currentUserName = useUserStore((s) => s.user?.name || s.user?.email || '');
  const currentUserPicture = useUserStore((s) => s.user?.picture ?? null);
  const sharedGroup = activeConv?.sharedGroup ?? null;
  const resolvedConversationId = conversationId ?? '';

  const hasMessages = messages.length > 0;
  const hasPreview = !!previewFile;
  const showTabs = hasMessages;
  const elevated = hasPreview;
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const setNarrowLayout = useChatStore((s) => s.setNarrowLayout);

  /* ---- 실제 콘텐츠 영역 너비 기준 narrow 감지 (사이드바 제외) ---- */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const narrow = entry.contentRect.width < NARROW_CONTENT_WIDTH;
      setIsNarrow(narrow);
      setNarrowLayout(narrow);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [setNarrowLayout]);

  const narrowPreviewView = useChatStore((s) => s.narrowPreviewView);
  const setNarrowPreviewView = useChatStore((s) => s.setNarrowPreviewView);

  // 새 메시지 전송 시 → 채팅 영역으로 전환
  const prevMessagesLength = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMessagesLength.current && isNarrow && hasPreview) {
      setNarrowPreviewView('chat');
    }
    prevMessagesLength.current = messages.length;
  }, [messages.length, isNarrow, hasPreview, setNarrowPreviewView]);


  /* ---- 탭 상태 ---- */
  const [activeTab, setActiveTab] = useState<'workspace' | 'chat'>('workspace');
  const prevHadMessages = useRef(false);

  // 첫 메시지 → 채팅 탭 자동 활성화
  useEffect(() => {
    if (hasMessages && !prevHadMessages.current) {
      setActiveTab('chat');
    }
    prevHadMessages.current = hasMessages;
  }, [hasMessages]);

  const messageIndexMap = useMemo(() => {
    const out = new Map<string, number>();
    messages.forEach((msg, idx) => out.set(msg.id, idx));
    return out;
  }, [messages]);

  const replyToByMessageId = useMemo(() => {
    const replyMap = new Map<string, ReplyToInfo>();
    if (!sharedGroup) return replyMap;

    const memberMap = new Map(sharedGroup.members.map((m) => [m.user_id, m]));
    const selfDisplayName = currentUserName.trim() || t('chat.me');
    let lastUserMessage: (typeof messages)[number] | null = null;
    let lastUserIndex = -1;

    for (let idx = 0; idx < messages.length; idx += 1) {
      const msg = messages[idx];
      if (msg.role === 'user') {
        lastUserMessage = msg;
        lastUserIndex = idx;
        continue;
      }
      if (msg.role !== 'assistant' || !lastUserMessage) continue;

      const isMe = !!lastUserMessage.senderId && lastUserMessage.senderId === currentUserId;
      const member = lastUserMessage.senderId
        ? memberMap.get(lastUserMessage.senderId)
        : null;
      const isImmediatePrev = lastUserIndex === idx - 1;
      const likelyMineWithoutSender = isImmediatePrev && !lastUserMessage.senderId && !lastUserMessage.senderName;
      const resolvedSenderName = isMe
        ? selfDisplayName
        : (lastUserMessage.senderName
            || member?.name
            || member?.email
            || (likelyMineWithoutSender ? selfDisplayName : t('chat.teamMember')));

      replyMap.set(msg.id, {
        messageId: lastUserMessage.id,
        senderName: resolvedSenderName,
        senderPicture: isMe
          ? currentUserPicture
          : (member?.picture ?? (likelyMineWithoutSender ? currentUserPicture : null)),
        content: lastUserMessage.content,
      });
    }
    return replyMap;
  }, [messages, sharedGroup, currentUserName, currentUserId, currentUserPicture]);

  const scrollToMessage = useCallback((messageId: string) => {
    const index = messageIndexMap.get(messageId);
    if (index === undefined) return;
    virtuosoRef.current?.scrollToIndex({
      index,
      align: 'center',
      behavior: 'smooth',
    });
  }, [messageIndexMap]);

  /* 파일 저장소의 "출처" 바로가기로 들어온 #m-{messageId} 해시를 처리해
   * Virtuoso 가 메시지 인덱스를 알게 되는 즉시 해당 버블로 스크롤한다. */
  const lookupMessageIndex = useCallback(
    (messageId: string) => messageIndexMap.get(messageId),
    [messageIndexMap],
  );
  const scrollByIndex = useCallback((index: number) => {
    virtuosoRef.current?.scrollToIndex({
      index,
      align: 'center',
      behavior: 'smooth',
    });
  }, []);
  useScrollToHashMessageByIndex(lookupMessageIndex, scrollByIndex, hasMessages);

  useEffect(() => {
    if (!hasMessages) return;
    if (streaming) {
      virtuosoRef.current?.scrollToIndex({
        index: messages.length - 1,
        align: 'end',
        behavior: 'auto',
      });
    }
  }, [messages.length, hasMessages, streaming, conversationId]);

  /* narrow 모드 표시 여부 계산 */
  const isNarrowPreviewHidden = isNarrow && hasPreview && narrowPreviewView === 'chat';
  const isNarrowContentHidden = isNarrow && hasPreview && narrowPreviewView === 'preview';

  return (
    <Wrapper ref={wrapperRef}>
      {/* ---- 좌측: 파일 프리뷰 (와이드: 6:4, 좁은 영역: 전체 or 숨김) ---- */}
      {hasPreview && (
        <PreviewPanel
          className="preview-embedded"
          $isNarrow={isNarrow}
          $narrowHidden={isNarrowPreviewHidden}
          $fullscreen={previewFullscreen}
        >
          <Suspense fallback={<PreviewPanelFallback />}>
            <FilePreviewPanel
              key={previewFile.id}
              file={previewFile}
              onClose={() => setPreviewFile(null)}
              fullscreen={previewFullscreen}
              onToggleFullscreen={togglePreviewFullscreen}
            />
          </Suspense>
        </PreviewPanel>
      )}

      {/* ---- 우측(또는 전체): 탭 컨테이너 ---- */}
      <ContentArea $hasPreview={hasPreview} $isNarrow={isNarrow} $narrowHidden={isNarrowContentHidden} $fullscreen={previewFullscreen}>
        <TabContainer $elevated={elevated}>
          {/* 탭 헤더 — 채팅 또는 메모장이 활성화된 후에만 표시 */}
          {showTabs && (
            <TabHeader>
              <Tab
                $active={activeTab === 'workspace'}
                onClick={() => setActiveTab('workspace')}
              >
                {t('workspace.transcription')}
              </Tab>
              {hasMessages && (
                <Tab
                  $active={activeTab === 'chat'}
                  onClick={() => setActiveTab('chat')}
                >
                  {t('workspace.chatTab')}
                </Tab>
              )}
            </TabHeader>
          )}

          {/* 트랜스크립션 (primaryContent) */}
          <TabBody $visible={!showTabs || activeTab === 'workspace'}>
            {primaryContent}
          </TabBody>

          {/* 채팅 */}
          {hasMessages && (
            <TabBody $visible={activeTab === 'chat'}>
              <MessagesScroll>
                <Suspense
                  fallback={
                    <div style={{ padding: '0 16px', maxWidth: '900px', margin: '0 auto' }}>
                      <MessageSkeleton />
                      <MessageSkeleton />
                    </div>
                  }
                >
                  <Virtuoso
                    ref={virtuosoRef}
                    style={{ height: '100%' }}
                    data={messages}
                    itemContent={(_, msg) => (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        sharedGroup={sharedGroup}
                        currentUserId={currentUserId}
              conversationId={resolvedConversationId}
                        onToggleReaction={toggleReaction}
                        replyTo={replyToByMessageId.get(msg.id) ?? null}
                        onScrollToMessage={scrollToMessage}
                        onRegenerate={
                          msg.role === 'assistant' && !msg.replacedByMessageId && resolvedConversationId
                            ? (messageId, forceMode) => regenerateMessage(resolvedConversationId, messageId, forceMode)
                            : undefined
                        }
                      />
                    )}
                    components={{
                      Footer: () => (
                        streaming && messages.length > 0 && messages[messages.length - 1].role === 'user'
                          ? (
                            <div style={{ padding: '0 16px', maxWidth: '900px', margin: '0 auto' }}>
                              <MessageSkeleton />
                            </div>
                          )
                          : null
                      ),
                    }}
                  />
                </Suspense>
              </MessagesScroll>
            </TabBody>
          )}

        </TabContainer>
      </ContentArea>
    </Wrapper>
  );
};
